<template>
  <SidebarSettingsGroup
    title="Style & Animation"
    collapsible
    :element-type="element.type"
    data-test="graphics-marqueeImageSettings-styleAndAnimation"
  >
    <div
      class="settings-row sidebar-form-group has-aspect-lock"
    >
      <div class="settings-col-12">
        <label class="sidebar-form-label">Size</label>
      </div>
      <div class="settings-col-5">
        <!-- type logic to avoid console warnings as width changes for text layouts -->
        <input
          id="sizeWidth"
          v-model.number="width"
          type="number"
          min="1"
          :max="5000"
          data-test="graphics-marqueeImageSettings-setWidth"
          data-entity="graphics-marqueeImageSettings-setWidth"
        >
        <label class="sidebar-input-sub-label">Width</label>
      </div>
      <div class="settings-col-2 text-center">
        <button
          tabindex="-1"
          class="btn-lock-aspect"
          :class="{ locked: element.aspectLock }"
          :data-balloon="getAspectLockBalloon()"
          data-balloon-pos="up"
          data-test="graphics-marqueeImageSettings-toggleAspectLock"
          data-entity="graphics-marqueeImageSettings-toggleAspectLock"
          @click.stop.prevent="toggleAspectLock"
        >
          <span :class="element.aspectLock ? 'fa-solid fa-lock' : 'fa-solid fa-unlock'" />
        </button>
      </div>
      <div class="settings-col-5">
        <!-- type logic to avoid console warnings as width changes for text layouts -->
        <input
          id="sizeHeight"
          v-model.number="height"
          type="number"
          min="0"
          :max="5000"
          data-test="graphics-marqueeImageSettings-setHeight"
          data-entity="graphics-marqueeImageSettings-setHeight"
        >
        <label class="sidebar-input-sub-label">Height</label>
      </div>
    </div>
    <template
      v-if="hideOpacityControls"
    >
      <opacity-settings v-model="opacity" />
      <hr class="sidebar-divider" />
    </template>

    <!-- Data Mapping Controls -->
    <template
      v-if="!isSocialIcon && !editingMultiple"
    >
      <div
        v-if="dataProvider"
        class="margin-top-md"
        data-test="graphics-marqueeImageSettings-mapData"
      >
        <p
          v-if="isMapped('imageUrl')"
          class="sidebar-form-label"
        >
          From Data Provider
        </p>
        <map-data-control
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          :mapped="isMapped('imageUrl')"
          property="imageUrl"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
      </div>

      <label
        v-if="dataProvider"
        class="sidebar-form-label"
        data-test="graphics-marqueeImageSettings-fallbackImage"
      >
        {{ isMapped('imageUrl') ? 'Fallback image' : 'Static image' }}
      </label>
      <asset-changer
        :preview-image="element.imageUrl"
        asset-type="image"
        @modal="$emit('modal', $event)"
        @select-asset="changeImage"
        @remove-asset="removeImage"
      />
    </template>

    <map-data-control
      v-if="isSocialIcon"
      :data-provider="dataProvider"
      :element="element"
      label="Network Icon Style"
      control-type="select"
      property="imageUrl"
      :mapped="isMapped('imageUrl')"
      :no-unmap="true"
      @update-map="mapToField"
      @clear-map="clearMapping"
    />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Border
        <input
          v-model="border"
          type="checkbox"
          data-test="graphics-marqueeImageSettings-toggleBorder"
          data-entity="graphics-marqueeImageSettings-toggleBorder"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div
        v-if="border"
        class="settings-row settings-row-xs"
      >
        <div class="settings-col-6 settings-col-xs">
          <validation-field as="input"
            v-model.number="borderWidth"
            rules="required|numeric|min:0"
            type="number"
            name="borderWidth"
            min="0"
            :class="{ 'is-invalid': validationErrors['borderWidth'] }"
            data-test="graphics-marqueeImageSettings-borderWidth"
          />
          <label class="sidebar-input-sub-label">Width</label>
        </div>
        <div class="settings-col-6 settings-col-xs colorpicker">
          <colorpicker v-model="borderColor" />
          <label class="sidebar-input-sub-label">Color</label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <box-shadow-settings :element="element"></box-shadow-settings>

    <hr class="sidebar-divider" />
  </SidebarSettingsGroup>
</template>

<script>
import SettingsMixins from '../../../mixins/settings-mixins';

import OpacitySettings from '../Opacity.vue';
import BoxShadowSettings from '../BoxShadow.vue';
import AssetChanger from '../AssetChanger.vue';
import MapDataControl from '../Data/MapDataControl.vue';

export default {
  emits: ['modal'],
  components: {
    'opacity-settings': OpacitySettings,
    'box-shadow-settings': BoxShadowSettings,
    AssetChanger,
    MapDataControl,
  },
  mixins: [SettingsMixins],
  props: {
    element: {
      type: Object,
      required: true,
    },
    scrollOffset: { required: true },
  },
  data() {
    return {
      maskPositions: [
        'top-left',
        'top-center',
        'top-right',
        'center-left',
        'center-center',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ],
    };
  },

  computed: {
    width: {
      get() {
        return this.element.styles.width;
      },
      set(v) {
        this.updateSize('width', v);
      },
    },

    height: {
      get() {
        return this.element.styles.height;
      },
      set(v) {
        this.updateSize('height', v);
      },
    },

    opacity: {
      get() {
        return this.element.styles.opacity;
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },

    border: {
      get() {
        return this.element.border;
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },

    borderWidth: {
      get() {
        return this.element.borderWidth;
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },

    borderColor: {
      get() {
        return (this.isMapped('borderColor') && this.dataValue('borderColor')) || this.element.borderColor;
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },

    borderTopLeftRadius: {
      get() {
        return this.element.borderTopLeftRadius;
      },
      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },

    borderTopRightRadius: {
      get() {
        return this.element.borderTopRightRadius;
      },
      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },

    borderBottomRightRadius: {
      get() {
        return this.element.borderBottomRightRadius;
      },
      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },

    borderBottomLeftRadius: {
      get() {
        return this.element.borderBottomLeftRadius;
      },
      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },

    hideOpacityControls() {
      return !this.isSocialIcon;
    },

    isSocialIcon() {
      return this.element.data?.type === 'social-network-attribution';
    },

  },

  methods: {
    changeImage({ asset, meta: imageMeta = {} }) {
      this.updateOneElementValue('imageUrl', asset?.media_url || '');
      this.updateOneElementValue('imageMeta', imageMeta);
    },

    removeImage() {
      this.updateOneElementValue('imageUrl', '');
      this.updateOneElementValue('imageMeta', {});
    },

    toggleAspectLock() {
      this.updateOneElementValue('aspectLock', !this.element.aspectLock);
    },

    getAspectLockBalloon() {
      return this.element.aspectLock ? 'Aspect ratio locked' : 'Freeform';
    },

    updateSize(propKey, propValue) {
      let newWidth;
      let newHeight;
      const origHeight = this.element.styles.height;
      const origWidth = this.element.styles.width;

      if (this.element.aspectLock) {
        if (propKey === 'width') {
          newWidth = propValue;
          newHeight = Math.round((newWidth * origHeight) / origWidth);
        } else {
          newHeight = propValue;
          newWidth = Math.round((newHeight * origWidth) / origHeight);
        }
        this.updateOneElementValue('styles.width', newWidth);
        this.updateOneElementValue('styles.height', newHeight);
      } else {
        this.updateOneElementValue(`styles.${propKey}`, propValue);
      }
    },
  },
};
</script>
